/**
 * One-time, idempotent purge of storage left behind by earlier persistence generations. Safe to run on every
 * startup: each check is cheap and every step is wrapped so a failure here can never break app boot.
 *
 * Generations purged:
 *
 * 1. Plain `localStorage` keys from the original pre-RxDB persistence: `horde.games.index` and any `horde.game.<id>`
 *    key.
 * 2. RxDB `localstorage` databases predating the current ID-only `horde-v4` release. This includes the original
 *    `horde` database and `horde-v3`, whose records intentionally have no compatibility path into the new schema.
 * 3. Dexie-backed IndexedDB databases from the RxDB `dexie` storage adapter that predated the switch to
 *    `getRxStorageLocalstorage()` (names look like `rxdb-dexie-horde--0--<collection>`).
 *
 * Delete this file (and its call site in app.config.ts) once enough time has passed that no returning users could
 * still have these legacy keys.
 */
export function cleanupLegacyStorage(): void {
    try {
        purgeLegacyLocalStorageKeys();
    } catch {
        // never block startup on cleanup failures
    }

    try {
        purgeLegacyIndexedDbDatabases();
    } catch {
        // never block startup on cleanup failures
    }
}

function purgeLegacyLocalStorageKeys(): void {
    if (typeof localStorage === 'undefined') {
        return;
    }

    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (key === null) {
            continue;
        }

        const isLegacyGameKey = key === 'horde.games.index' || key.startsWith('horde.game.');
        const isLegacyRxdbLocalstorageKey =
            key.startsWith('RxDB-ls-') && (key.includes('-horde--') || key.includes('-horde-v3--'));

        if (isLegacyGameKey || isLegacyRxdbLocalstorageKey) {
            keysToRemove.push(key);
        }
    }

    for (const key of keysToRemove) {
        try {
            localStorage.removeItem(key);
        } catch {
            // best-effort per key
        }
    }
}

function purgeLegacyIndexedDbDatabases(): void {
    if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') {
        return;
    }

    // Fire-and-forget: startup must not wait on this.
    indexedDB
        .databases()
        .then((databases) => {
            for (const { name } of databases) {
                if (!name?.startsWith('rxdb-dexie-horde--')) {
                    continue;
                }

                try {
                    indexedDB.deleteDatabase(name);
                } catch {
                    // best-effort per database
                }
            }
        })
        .catch(() => {
            // best-effort
        });
}
